import { useState, useRef, useEffect, useMemo, useCallback, type ReactNode, type KeyboardEvent } from 'react';
import type { Conversation, User, Epic, IssueType, IssueSummary } from '../api';
import {
  streamMessage,
  createTicket,
  fetchEpics,
  fetchIssueTypes,
  fetchMessages,
  generateTicketDraft,
  generateTicketUpdateDraft,
  updateTicket,
  searchIssues,
} from '../api';
import { Mascot } from './Mascot';
import './Chat.css';

interface Template {
  id: string;
  label: string;
  icon: string;
  skeleton: string;
}

const TEMPLATES: Template[] = [
  {
    id: 'bug',
    label: 'Bug',
    icon: '🐛',
    skeleton:
      'Encontré un problema: [qué está fallando y dónde].\n\nPasos para reproducirlo: [...]\n\nLo esperado sería: [...]',
  },
  {
    id: 'feature',
    label: 'Nueva funcionalidad',
    icon: '✨',
    skeleton:
      'Necesito una funcionalidad nueva: [qué debería poder hacer el usuario].\n\nEsto es para: [qué objetivo resuelve].',
  },
  {
    id: 'ux',
    label: 'Mejora de UX',
    icon: '🎨',
    skeleton:
      'Quiero mejorar la experiencia de: [qué pantalla o flujo].\n\nEl problema actual es: [qué genera fricción].\n\nMe gustaría que: [cómo debería sentirse/verse].',
  },
  {
    id: 'tech-debt',
    label: 'Deuda técnica',
    icon: '🔧',
    skeleton:
      'Hay una deuda técnica en: [qué parte del sistema].\n\nEl riesgo de no atenderla es: [...]\n\nLa mejora propuesta sería: [...]',
  },
];

interface ChatProps {
  conversation: Conversation;
  user: User;
  onBack: () => void;
  onTicketCreated: (conversationId: string, key: string, url: string, title: string) => void;
  onTicketUpdated: (conversationId: string) => void;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  time: string;
}

function nowLabel(): string {
  return new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

// Non-blocking nudge, not a hard rule: flags acceptance criteria that look too short or
// use a vague adjective without any concrete/verifiable cue (a number or a clear condition).
const VAGUE_TERMS =
  /\b(bien|rápido|rapido|fácil|facil|intuitivo|adecuado|correctamente|óptimo|optimo|eficiente|robusto|amigable|sencillo|claro|usable)\b/i;
const CONCRETE_CUES = /\d|cuando|si\s|debe (mostrar|permitir|validar|enviar|redirigir|guardar|bloquear|calcular)/i;

function findWeakCriteria(description: string): string[] {
  const match = description.match(/\*Criterios de Aceptaci[oó]n:?\*\s*\n((?:-.*\n?)*)/i);
  if (!match) return [];
  const lines = match[1]
    .split('\n')
    .map((line) => line.replace(/^-\s*/, '').trim())
    .filter(Boolean);

  return lines.filter((line) => {
    const wordCount = line.split(/\s+/).filter(Boolean).length;
    if (wordCount < 5) return true;
    return VAGUE_TERMS.test(line) && !CONCRETE_CUES.test(line);
  });
}

// Renders the bounded subset of markdown the agent is instructed to use (*bold*, "- " lists,
// blank-line paragraphs) — not a general markdown parser, matches AgentService's prompt format.
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text
    .split(/(\*[^*]+\*)/g)
    .filter((token) => token.length > 0)
    .map((token, i) => {
      const boldMatch = token.match(/^\*(.+)\*$/);
      if (boldMatch) {
        return <strong key={`${keyPrefix}-${i}`}>{boldMatch[1]}</strong>;
      }
      return <span key={`${keyPrefix}-${i}`}>{token}</span>;
    });
}

function renderMessageContent(content: string): ReactNode {
  const blocks = content.split('\n\n');
  const nodes: ReactNode[] = [];
  let blockIndex = 0;

  for (const block of blocks) {
    const lines = block.split('\n').filter((line) => line.length > 0);
    let paragraphLines: string[] = [];
    let listLines: string[] = [];

    const flushParagraph = () => {
      if (paragraphLines.length === 0) return;
      const key = `p-${blockIndex++}`;
      nodes.push(
        <p key={key} className="md-paragraph">
          {paragraphLines.map((line, i) => (
            <span key={i}>
              {renderInline(line, `${key}-${i}`)}
              {i < paragraphLines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
      paragraphLines = [];
    };

    const flushList = () => {
      if (listLines.length === 0) return;
      const key = `ul-${blockIndex++}`;
      nodes.push(
        <ul key={key} className="md-list">
          {listLines.map((line, i) => (
            <li key={i}>{renderInline(line.replace(/^-\s+/, ''), `${key}-${i}`)}</li>
          ))}
        </ul>
      );
      listLines = [];
    };

    for (const line of lines) {
      if (/^-\s+/.test(line)) {
        flushParagraph();
        listLines.push(line);
      } else {
        flushList();
        paragraphLines.push(line);
      }
    }
    flushParagraph();
    flushList();
  }

  return nodes;
}

export function Chat({ conversation, user, onBack, onTicketCreated, onTicketUpdated }: ChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [epicsLoading, setEpicsLoading] = useState(true);
  const [epicsError, setEpicsError] = useState(false);
  const [epicQuery, setEpicQuery] = useState('');
  const [selectedEpic, setSelectedEpic] = useState<Epic | null>(null);
  const [showEpicDropdown, setShowEpicDropdown] = useState(false);
  const [highlightedEpicIndex, setHighlightedEpicIndex] = useState(-1);
  const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSummary, setDraftSummary] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [relatedQuery, setRelatedQuery] = useState('');
  const [relatedResults, setRelatedResults] = useState<IssueSummary[]>([]);
  const [selectedRelated, setSelectedRelated] = useState<IssueSummary[]>([]);
  const [showRelatedDropdown, setShowRelatedDropdown] = useState(false);
  const [showTicketFlow, setShowTicketFlow] = useState(false);
  const [showUpdateFlow, setShowUpdateFlow] = useState(false);
  const [isGeneratingUpdateDraft, setIsGeneratingUpdateDraft] = useState(false);
  const [hasUpdateDraft, setHasUpdateDraft] = useState(false);
  const [updateSummary, setUpdateSummary] = useState('');
  const [updateDescription, setUpdateDescription] = useState('');
  const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Reopening a past conversation (sidebar select, or a fresh clone) reuses this same
  // component instance rather than remounting it, so both the message history and every
  // piece of per-conversation draft/picker state need an explicit reset here.
  useEffect(() => {
    setInput('');
    setError(null);
    setSelectedEpic(null);
    setEpicQuery('');
    setSelectedIssueType('');
    setHasDraft(false);
    setDraftSummary('');
    setDraftDescription('');
    setSelectedRelated([]);
    setRelatedQuery('');
    setShowTicketFlow(false);
    setShowUpdateFlow(false);
    setHasUpdateDraft(false);
    setUpdateSummary('');
    setUpdateDescription('');
    setUpdateSuccess(false);

    let cancelled = false;
    fetchMessages(conversation.id)
      .then((stored) => {
        if (cancelled) return;
        setMessages(
          stored.map((m) => ({
            id: m.id,
            role: m.role === 'user' ? 'user' : 'assistant',
            content: m.content,
            time: new Date(m.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      });

    return () => {
      cancelled = true;
    };
  }, [conversation.id]);

  useEffect(() => {
    const query = relatedQuery.trim();
    if (query.length < 2) {
      setRelatedResults([]);
      return;
    }
    const handle = setTimeout(() => {
      searchIssues(query)
        .then(setRelatedResults)
        .catch(() => setRelatedResults([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [relatedQuery]);

  useEffect(() => {
    if (conversation.status === 'created') return;
    setEpicsLoading(true);
    setEpicsError(false);
    fetchEpics()
      .then(setEpics)
      .catch(() => setEpicsError(true))
      .finally(() => setEpicsLoading(false));
    fetchIssueTypes()
      .then(setIssueTypes)
      .catch(() => setIssueTypes([]));
  }, [conversation.status]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    }
  }, [input]);

  useEffect(() => {
    if (!updateSuccess) return;
    const handle = setTimeout(() => setUpdateSuccess(false), 4000);
    return () => clearTimeout(handle);
  }, [updateSuccess]);

  const handleSend = useCallback(() => {
    if (!input.trim() || isStreaming) return;
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      time: nowLabel(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setError(null);

    let assistantMessage = '';
    const assistantId = (Date.now() + 1).toString();
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '', time: nowLabel() },
    ]);

    streamMessage(
      conversation.id,
      userMessage.content,
      (chunk) => {
        assistantMessage += chunk;
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: assistantMessage } : m))
        );
      },
      () => {
        setIsStreaming(false);
        setAnnouncement(assistantMessage);
      },
      (err) => {
        setError(err);
        setIsStreaming(false);
      }
    );
  }, [input, isStreaming, conversation.id]);

  const handleGenerateDraft = async () => {
    try {
      setError(null);
      setIsGeneratingDraft(true);
      const result = await generateTicketDraft(conversation.id);
      setDraftSummary(result.summary);
      setDraftDescription(result.description);
      setHasDraft(true);
      if (!selectedIssueType) {
        const match = issueTypes.find((t) => t.name.toLowerCase() === result.issueType.toLowerCase());
        setSelectedIssueType(match ? match.name : issueTypes[0]?.name || result.issueType);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generando la vista previa del ticket');
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const handleCreateTicket = async () => {
    try {
      setError(null);
      setIsCreatingTicket(true);
      const result = await createTicket(conversation.id, {
        epicKey: selectedEpic?.key,
        summary: draftSummary,
        description: draftDescription,
        issueType: selectedIssueType,
        relatedKeys: selectedRelated.map((issue) => issue.key),
      });
      onTicketCreated(conversation.id, result.key, result.url, result.title);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error creando ticket');
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const weakCriteria = useMemo(() => findWeakCriteria(draftDescription), [draftDescription]);

  const handleGenerateUpdateDraft = async () => {
    try {
      setError(null);
      setIsGeneratingUpdateDraft(true);
      const result = await generateTicketUpdateDraft(conversation.id);
      setUpdateSummary(result.summary);
      setUpdateDescription(result.description);
      setHasUpdateDraft(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generando la vista previa de la actualización');
    } finally {
      setIsGeneratingUpdateDraft(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setError(null);
      setIsUpdatingTicket(true);
      await updateTicket(conversation.id, { summary: updateSummary, description: updateDescription });
      setHasUpdateDraft(false);
      setShowUpdateFlow(false);
      setUpdateSuccess(true);
      onTicketUpdated(conversation.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando el ticket');
    } finally {
      setIsUpdatingTicket(false);
    }
  };

  const updateWeakCriteria = useMemo(() => findWeakCriteria(updateDescription), [updateDescription]);

  const addRelated = (issue: IssueSummary) => {
    setSelectedRelated((prev) => (prev.some((i) => i.key === issue.key) ? prev : [...prev, issue]));
    setRelatedQuery('');
    setRelatedResults([]);
    setShowRelatedDropdown(false);
  };

  const removeRelated = (key: string) => {
    setSelectedRelated((prev) => prev.filter((issue) => issue.key !== key));
  };

  const handleRelatedKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && relatedResults.length > 0) {
      e.preventDefault();
      addRelated(relatedResults[0]);
    } else if (e.key === 'Escape') {
      setShowRelatedDropdown(false);
    }
  };

  const startTemplate = (template: Template) => {
    setInput(template.skeleton);
    textareaRef.current?.focus();
  };

  const filteredEpics = epics.filter((epic) =>
    `${epic.key} ${epic.summary}`.toLowerCase().includes(epicQuery.toLowerCase())
  );

  const selectEpic = (epic: Epic) => {
    setSelectedEpic(epic);
    setEpicQuery('');
    setShowEpicDropdown(false);
    setHighlightedEpicIndex(-1);
  };

  const handleEpicKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showEpicDropdown || filteredEpics.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedEpicIndex((i) => (i + 1) % filteredEpics.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedEpicIndex((i) => (i <= 0 ? filteredEpics.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (highlightedEpicIndex >= 0 && highlightedEpicIndex < filteredEpics.length) {
        e.preventDefault();
        selectEpic(filteredEpics[highlightedEpicIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowEpicDropdown(false);
      setHighlightedEpicIndex(-1);
    }
  };

  return (
    <div className="chat">
      <div className="chat-header">
        <div className="chat-header-left">
          <button className="back-button" onClick={onBack} aria-label="Volver">
            ←
          </button>
          <div className="chat-avatar">
            <Mascot expression={conversation.status === 'created' ? 'success' : 'idle'} size={32} />
          </div>
          <div className="chat-info">
            <h3>{conversation.title}</h3>
            <div className="chat-status">
              <span className="chat-status-dot" />
              <span>Agente BK en línea</span>
            </div>
          </div>
        </div>
        <div className="chat-header-right">
          <span className={`chat-stage-pill ${conversation.status}`}>
            {conversation.status === 'created' ? 'Ticket creado' : 'En refinamiento'}
          </span>
          {conversation.ticketKey && (
            <a
              href={conversation.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-ticket"
            >
              🎫 {conversation.ticketKey}
            </a>
          )}
        </div>
      </div>

      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>

      <div className="chat-messages" role="log" aria-label="Historial de conversación">
        {messages.length === 0 && (
          <div className="welcome">
            <span className="welcome-icon">👋</span>
            <h3>Hola {user.displayName}</h3>
            <p>Cuéntame qué necesitas. Te ayudaré a crear un ticket completo y una especificación técnica.</p>
            <div className="welcome-tips">
              <div className="welcome-tip">
                <span className="welcome-tip-index">1</span>
                <span>Explica el problema o requerimiento</span>
              </div>
              <div className="welcome-tip">
                <span className="welcome-tip-index">2</span>
                <span>Responde mis preguntas de refinamiento</span>
              </div>
              <div className="welcome-tip">
                <span className="welcome-tip-index">3</span>
                <span>Confirma y creo el ticket en Jira</span>
              </div>
            </div>
            <div className="welcome-templates">
              <span className="welcome-templates-label">O empieza con un punto de partida</span>
              <div className="welcome-templates-grid">
                {TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className="welcome-template-button"
                    onClick={() => startTemplate(template)}
                  >
                    <span className="welcome-template-icon">{template.icon}</span>
                    <span>{template.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message ${msg.role === 'user' ? 'user' : 'assistant'}`}
          >
            <div className="message-avatar">
              {msg.role === 'user' ? '👤' : <Mascot expression="idle" size={26} />}
            </div>
            <div className="message-stack">
              <div className="bubble">
                <div className="message-text">{renderMessageContent(msg.content)}</div>
              </div>
              <span className="message-time">{msg.time}</span>
            </div>
          </div>
        ))}
        {isStreaming && (
          <div className="message assistant">
            <div className="message-avatar">
              <Mascot expression="thinking" size={26} />
            </div>
            <div className="bubble typing-bubble">
              <div className="typing-dot" />
              <div className="typing-dot" />
              <div className="typing-dot" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && <div className="chat-error">⚠️ {error}</div>}

      <div className="chat-input-area">
        {conversation.status === 'created' && conversation.ticketKey && (
          <>
            <div className="ticket-banner">
              <span className="ticket-banner-icon">✅</span>
              <span>
                Ticket creado:{" "}
                <a href={conversation.ticketUrl} target="_blank" rel="noopener noreferrer">
                  {conversation.ticketKey}
                </a>
              </span>
            </div>

            {updateSuccess && (
              <div className="ticket-banner update-success">
                <span className="ticket-banner-icon">🔄</span>
                <span>Ticket actualizado y spec.md regenerado.</span>
              </div>
            )}

            {!showUpdateFlow ? (
              <button
                type="button"
                className="update-ticket-toggle"
                onClick={() => setShowUpdateFlow(true)}
              >
                ✏️ Actualizar este ticket
              </button>
            ) : (
              <div className="ticket-step">
                <div className="ticket-step-header">
                  <span className="ticket-step-icon">🔄</span>
                  <div className="ticket-step-heading">
                    <span className="ticket-step-title">Actualizar ticket {conversation.ticketKey}</span>
                    <span className="ticket-step-subtitle">
                      {hasUpdateDraft
                        ? 'Revisa y ajusta el texto — reemplaza la descripción actual en Jira y regenera el spec.md.'
                        : 'Genera una vista previa con lo que hayas conversado desde que se creó el ticket.'}
                    </span>
                  </div>
                </div>

                <div className="ticket-step-body">
                  {!hasUpdateDraft ? (
                    <div className="ticket-step-actions">
                      <button
                        type="button"
                        className="ticket-regenerate-button"
                        onClick={() => setShowUpdateFlow(false)}
                      >
                        Cancelar
                      </button>
                      <button
                        className="create-ticket-button"
                        onClick={handleGenerateUpdateDraft}
                        disabled={isGeneratingUpdateDraft}
                      >
                        <span>🔄</span>
                        <span>{isGeneratingUpdateDraft ? 'Generando vista previa...' : 'Generar actualización'}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="ticket-preview">
                      <div className="ticket-preview-field">
                        <label className="field-label" htmlFor="update-summary">
                          Título
                        </label>
                        <input
                          id="update-summary"
                          type="text"
                          value={updateSummary}
                          onChange={(e) => setUpdateSummary(e.target.value)}
                        />
                      </div>
                      <div className="ticket-preview-field">
                        <label className="field-label" htmlFor="update-description">
                          Descripción
                        </label>
                        <textarea
                          id="update-description"
                          className="ticket-preview-textarea"
                          value={updateDescription}
                          onChange={(e) => setUpdateDescription(e.target.value)}
                          rows={12}
                        />
                      </div>

                      {updateWeakCriteria.length > 0 && (
                        <div className="weak-criteria-warning" role="status">
                          ⚠️{' '}
                          {updateWeakCriteria.length === 1
                            ? '1 criterio parece poco verificable'
                            : `${updateWeakCriteria.length} criterios parecen poco verificables`}
                          {' — considera agregar un número, una condición o un resultado concreto.'}
                        </div>
                      )}

                      <div className="ticket-step-actions">
                        <button
                          type="button"
                          className="ticket-regenerate-button"
                          onClick={handleGenerateUpdateDraft}
                          disabled={isGeneratingUpdateDraft}
                        >
                          {isGeneratingUpdateDraft ? 'Regenerando...' : '🔄 Regenerar'}
                        </button>
                        <button
                          className="create-ticket-button"
                          onClick={handleUpdateTicket}
                          disabled={isUpdatingTicket || !updateSummary.trim()}
                        >
                          <span>✅</span>
                          <span>{isUpdatingTicket ? 'Actualizando...' : 'Confirmar actualización'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {conversation.status !== 'created' && showTicketFlow && (
          <div className="ticket-step">
            <div className="ticket-step-header">
              <span className="ticket-step-icon">🎯</span>
              <div className="ticket-step-heading">
                <span className="ticket-step-title">¿Listo para crear el ticket?</span>
                <span className="ticket-step-subtitle">
                  {hasDraft
                    ? 'Revisa y ajusta el texto — es exactamente lo que se va a enviar a Jira.'
                    : 'Vincula una épica y el tipo de issue (opcional) y genera una vista previa.'}
                </span>
              </div>
              {!hasDraft && (
                <button
                  type="button"
                  className="ticket-step-close"
                  aria-label="Cerrar"
                  onClick={() => setShowTicketFlow(false)}
                >
                  ✕
                </button>
              )}
            </div>

            <div className="ticket-step-body">
              <div className="epic-picker">
                <label className="field-label" htmlFor="epic-search">
                  Épica
                </label>
                {epicsLoading ? (
                  <p className="field-hint">Cargando épicas…</p>
                ) : epicsError ? (
                  <p className="field-hint field-hint-error">
                    No se pudieron cargar las épicas — puedes continuar sin vincular una.
                  </p>
                ) : epics.length === 0 ? (
                  <p className="field-hint">Este proyecto no tiene épicas todavía.</p>
                ) : (
                  <>
                    <div className="epic-input-wrap">
                      <input
                        id="epic-search"
                        type="text"
                        role="combobox"
                        aria-expanded={showEpicDropdown && filteredEpics.length > 0}
                        aria-controls="epic-listbox"
                        aria-autocomplete="list"
                        aria-activedescendant={
                          highlightedEpicIndex >= 0 && filteredEpics[highlightedEpicIndex]
                            ? `epic-option-${filteredEpics[highlightedEpicIndex].key}`
                            : undefined
                        }
                        placeholder="Buscar épica (opcional)"
                        value={selectedEpic ? `${selectedEpic.key} — ${selectedEpic.summary}` : epicQuery}
                        onChange={(e) => {
                          setSelectedEpic(null);
                          setEpicQuery(e.target.value);
                          setShowEpicDropdown(true);
                          setHighlightedEpicIndex(-1);
                        }}
                        onFocus={() => setShowEpicDropdown(true)}
                        onBlur={() => {
                          setShowEpicDropdown(false);
                          setHighlightedEpicIndex(-1);
                        }}
                        onKeyDown={handleEpicKeyDown}
                      />
                      {selectedEpic && (
                        <button
                          type="button"
                          className="epic-clear"
                          aria-label="Quitar épica"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedEpic(null);
                            setEpicQuery('');
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {showEpicDropdown && filteredEpics.length > 0 && (
                      <ul className="epic-dropdown" id="epic-listbox" role="listbox">
                        {filteredEpics.map((epic, index) => (
                          <li
                            key={epic.key}
                            id={`epic-option-${epic.key}`}
                            role="option"
                            aria-selected={index === highlightedEpicIndex}
                            className={index === highlightedEpicIndex ? 'active' : ''}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectEpic(epic);
                            }}
                            onMouseEnter={() => setHighlightedEpicIndex(index)}
                          >
                            <strong>{epic.key}</strong> {epic.summary}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>

              {issueTypes.length > 0 && (
                <div className="issue-type-picker">
                  <label className="field-label" htmlFor="issue-type-select">
                    Tipo de issue
                  </label>
                  <select
                    id="issue-type-select"
                    value={selectedIssueType}
                    onChange={(e) => setSelectedIssueType(e.target.value)}
                  >
                    <option value="">Dejar que el agente decida</option>
                    {issueTypes.map((type) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="related-issues-picker">
                <label className="field-label" htmlFor="related-search">
                  Issues relacionados
                </label>
                {selectedRelated.length > 0 && (
                  <div className="related-issues-chips">
                    {selectedRelated.map((issue) => (
                      <span key={issue.key} className="related-issue-chip">
                        {issue.key}
                        <button
                          type="button"
                          aria-label={`Quitar ${issue.key}`}
                          onClick={() => removeRelated(issue.key)}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="epic-input-wrap">
                  <input
                    id="related-search"
                    type="text"
                    placeholder="Buscar por clave o texto (mín. 2 caracteres)"
                    value={relatedQuery}
                    onChange={(e) => {
                      setRelatedQuery(e.target.value);
                      setShowRelatedDropdown(true);
                    }}
                    onFocus={() => setShowRelatedDropdown(true)}
                    onBlur={() => setShowRelatedDropdown(false)}
                    onKeyDown={handleRelatedKeyDown}
                  />
                </div>
                {showRelatedDropdown && relatedResults.length > 0 && (
                  <ul className="epic-dropdown">
                    {relatedResults
                      .filter((issue) => !selectedRelated.some((s) => s.key === issue.key))
                      .map((issue) => (
                        <li
                          key={issue.key}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            addRelated(issue);
                          }}
                        >
                          <strong>{issue.key}</strong> {issue.summary}
                        </li>
                      ))}
                  </ul>
                )}
              </div>

              {!hasDraft ? (
                <button
                  className="create-ticket-button"
                  onClick={handleGenerateDraft}
                  disabled={isGeneratingDraft}
                >
                  <span>🎫</span>
                  <span>{isGeneratingDraft ? 'Generando vista previa...' : 'Generar vista previa del ticket'}</span>
                </button>
              ) : (
                <div className="ticket-preview">
                  <div className="ticket-preview-field">
                    <label className="field-label" htmlFor="draft-summary">
                      Título
                    </label>
                    <input
                      id="draft-summary"
                      type="text"
                      value={draftSummary}
                      onChange={(e) => setDraftSummary(e.target.value)}
                    />
                  </div>
                  <div className="ticket-preview-field">
                    <label className="field-label" htmlFor="draft-description">
                      Descripción
                    </label>
                    <textarea
                      id="draft-description"
                      className="ticket-preview-textarea"
                      value={draftDescription}
                      onChange={(e) => setDraftDescription(e.target.value)}
                      rows={12}
                    />
                  </div>

                  {weakCriteria.length > 0 && (
                    <div className="weak-criteria-warning" role="status">
                      ⚠️{' '}
                      {weakCriteria.length === 1
                        ? '1 criterio parece poco verificable'
                        : `${weakCriteria.length} criterios parecen poco verificables`}
                      {' — considera agregar un número, una condición o un resultado concreto.'}
                    </div>
                  )}

                  <div className="ticket-step-actions">
                    <button
                      type="button"
                      className="ticket-regenerate-button"
                      onClick={handleGenerateDraft}
                      disabled={isGeneratingDraft}
                    >
                      {isGeneratingDraft ? 'Regenerando...' : '🔄 Regenerar'}
                    </button>
                    <button
                      className="create-ticket-button"
                      onClick={handleCreateTicket}
                      disabled={isCreatingTicket || !draftSummary.trim()}
                    >
                      <span>✅</span>
                      <span>{isCreatingTicket ? 'Creando ticket...' : 'Confirmar y crear ticket'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="input-row">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Escribe tu mensaje..."
            rows={1}
          />
          {conversation.status !== 'created' && messages.length > 0 && !showTicketFlow && (
            <button
              type="button"
              className="open-ticket-flow-button"
              aria-label="Crear ticket"
              onClick={() => setShowTicketFlow(true)}
            >
              <span aria-hidden="true">🎫</span>
              <span>Crear ticket</span>
            </button>
          )}
          <button onClick={handleSend} disabled={!input.trim() || isStreaming} aria-label="Enviar">
            ➤
          </button>
        </div>
        <div className="input-hint">Enter para enviar · Shift+Enter para nueva línea</div>
      </div>
    </div>
  );
}
